/**
 * Reads the AIChatConversation context.
 *
 * Isolated read path for the Conversation slice (the mode-agnostic chat engine + `mode`, plus
 * setters). Throws if used outside its provider — pass `{ optional: true }` to get `null`
 * instead (for leaf consumers that must render gracefully with no AIChatProvider, e.g.
 * read-only message display). Both forms subscribe to the same context; only the throw is gated.
 *
 * Consumers that only dispatch should use useAIChatConversationActions() instead, so they
 * subscribe to the stable action object and skip state re-renders.
 */
import { useContext } from 'react';
import { AIChatConversationContext } from './AIChatConversationProvider';
import type { AIChatConversationValue } from './AIChatConversationProvider';

type UseAIChatConversationOptions = {
  optional?: boolean;
};

export function useAIChatConversation(): AIChatConversationValue;
export function useAIChatConversation(options: { optional: true }): AIChatConversationValue | null;
export function useAIChatConversation(options: UseAIChatConversationOptions = {}): AIChatConversationValue | null {
  const context = useContext(AIChatConversationContext);
  if (!context && !options.optional) {
    throw new Error('useAIChatConversation must be used within a AIChatConversationProvider');
  }
  return context;
}
